import { ServiceLocator } from '../../app/ServiceLocator';
import { WebClientDetailFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientDetailFetcher';
import { WebClientListResourceFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientListResourceFetcher';
import { RequestHelper } from '../../ts/helpers/RequestHelper';
import { ResultHelper } from '../../ts/helpers/ResultHelper';
import { Sanitizer } from '../../ts/helpers/Sanitizer';
import { ServerApiMapper } from '../../ts/helpers/ServerModelHelper';
import { ProjectMapper } from '../../ts/mappers/ProjectMapper';
import { IProjectDetailInfo, IProjectInfo } from '../../ts/models/IProjectInfo';
import { INamedItemInfo } from '../../ts/models/resourceInfoParts';
import { FieldValuesQuery } from '../../ts/resources/FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from '../../ts/resources/Resource';
import { ResourceQuery } from '../../ts/resources/ResourceQuery';
import { CreateProjectRequest, IProjectIdResult } from '../../ts/ServerApi/requests/CreateProjectRequest';
import { DeleteProjectsRequest } from '../../ts/ServerApi/requests/DeleteProjectsRequest';
import { GetFieldValuesRequest } from '../../ts/ServerApi/requests/GetFieldValuesRequest';
import { GetProjectActionsRequest } from '../../ts/ServerApi/requests/GetProjectActionsRequest';
import {
  GetProjectAncestorsRequest,
  IGetProjectAncestorsResult
} from '../../ts/ServerApi/requests/GetProjectAncestorsRequest';
import { GetProjectsRequest } from '../../ts/ServerApi/requests/GetProjectsRequest';
import { MoveProjectsToProjectRequest } from '../../ts/ServerApi/requests/MoveProjectsToProjectRequest';
import {
  ISetContentControlledPermissionsParams,
  SetContentControlledPermissionsRequest
} from '../../ts/ServerApi/requests/SetContentControlledPermissionsRequest';
import {
  ISetProjectControlledPermissionsParams,
  SetProjectControlledPermissionsRequest
} from '../../ts/ServerApi/requests/SetProjectControlledPermissionsRequest';
import { SetProjectDescriptionRequest } from '../../ts/ServerApi/requests/SetProjectDescriptionRequest';
import { SetProjectNameRequest } from '../../ts/ServerApi/requests/SetProjectNameRequest';
import { SetProjectsOwnerRequest } from '../../ts/ServerApi/requests/SetProjectsOwnerRequest';
import { IActionResult } from '../../ts/ServerApi/types/IActionResult';
import { IProjectActions } from '../../ts/ServerApi/types/IProjectActions';
import { IEmptyResult, IResult } from '../../ts/ServerApi/types/IResponse';
import { ErrorNotifyingServer } from '../../ts/services/ErrorNotifyingServer';
import { ServerService } from '../../ts/services/ServerService';

export class ProjectsResource implements IListResource<IProjectInfo>, IFieldValuesResource, IDetailResource<IProjectDetailInfo>,
    IActionResource<IProjectActions> {
  private listFetcher = new WebClientListResourceFetcher(GetProjectsRequest, ServerApiMapper.projects);
  private detailFetcher = new WebClientDetailFetcher('getProject', ProjectMapper.mapDetail);
  private fieldValuesFetcher = new WebClientListResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);

  public all(): ResourceQuery<IProjectInfo> {
    return new ResourceQuery(this.listFetcher);
  }

  public getById(id: string): ng.IPromise<IProjectDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'project');
  }

  // Actions

  public actions(id: string): ng.IPromise<IProjectActions> {
    return ServiceLocator.get<ServerService>('ServerService')
      .sendRequest(new GetProjectActionsRequest(id));
  }

  public del(ids: string[]): ng.IPromise<IResult> {
    return ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer')
      .sendRequestsAndDelay(
        RequestHelper.buildRequests(ids, (idsBlock) => new DeleteProjectsRequest(idsBlock)),
        ResultHelper.combineResults
      );
  }

  public rename(id: string, name: string): ng.IPromise<IResult> {
    return ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer')
      .sendRequestAndDelay(new SetProjectNameRequest(id, name));
  }

  public setDescription(id: string, description: string): ng.IPromise<IResult> {
    return ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer')
      .sendRequest(new SetProjectDescriptionRequest(id, description));
  }

  public setProjectControlledPermissions(params: ISetProjectControlledPermissionsParams): ng.IPromise<IEmptyResult> {
    return ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer')
      .sendRequest(new SetProjectControlledPermissionsRequest(params));
  }

  public setContentControlledPermissions(params: ISetContentControlledPermissionsParams): ng.IPromise<IActionResult> {
    return ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer')
      .sendRequest(new SetContentControlledPermissionsRequest(params));
  }

  public setOwner(ids: string[], ownerId: string): ng.IPromise<IResult> {
    return ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer')
      .sendRequestsAndDelay(
        RequestHelper.buildRequests(ids, (idsBlock) => new SetProjectsOwnerRequest(idsBlock, ownerId)),
        ResultHelper.combineResults
      );
  }

  /**
   * Returns all the ancestors in the nested project chain from the top level,
   * including the project with the given ID.
   */
  public getProjectHierarchy(id: string): ng.IPromise<INamedItemInfo[]> {
    return ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer')
      .sendRequest(new GetProjectAncestorsRequest(id))
      .then((result: IGetProjectAncestorsResult) => result.projects ? result.projects : [])
      .catch(_ => []);
  }

  public moveToProject(targetProjectId: string, projectIds: string[]): ng.IPromise<IResult> {
    const requests = RequestHelper.buildRequests(
      projectIds,
      idsBlock => new MoveProjectsToProjectRequest({ ids: idsBlock, projectId: targetProjectId })
    );
    return ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer')
      .sendRequestsAndDelay(requests, ResultHelper.combineResults);
  }

  public create(name: string, description?: string, parentProjectId?: string): ng.IPromise<IProjectIdResult> {
    const request = new CreateProjectRequest({
      name: name,
      description: Sanitizer.trimTextOrUndefined(description),
      parentProjectId: parentProjectId
    });
    return ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer')
      .sendRequestAndDelay(request);
  }
}

export const Projects = new ProjectsResource();
